import { useEffect, useState } from 'react';
import { MarketingOrderRow } from './marketingOrdersView';

export const ORDERS_PAGE_SIZE = 10;

export type LoadRowOrders<R> = (row: R, page: number) => Promise<{ orders: MarketingOrderRow[]; totalPages: number }>;

/**
 * One row of an executive table open at a time, its orders fetched when it is
 * opened and paged on their own — like a paid order's cycle view. Shared by
 * the marketing and farm executive tables so both behave identically.
 *
 * Re-fetches the open row when `loadOrders` changes (the page filters did) or
 * when `rows` is reloaded (e.g. after an approval), so it never goes stale.
 */
export function useExpandedOrders<R>(rows: R[], rowKey: (r: R) => string, loadOrders: LoadRowOrders<R>) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<MarketingOrderRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const row = rows.find((r) => rowKey(r) === expandedKey);
    if (!row) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadOrders(row, page)
      .then((res) => {
        if (cancelled) return;
        setOrders(res.orders);
        setTotalPages(res.totalPages);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setOrders([]);
        setError(e?.response?.data?.detail || e?.message || 'Failed to load orders');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // rowKey is a pure accessor; the row is identified by expandedKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedKey, page, loadOrders, rows]);

  const toggle = (key: string) => {
    setPage(1);
    setOrders([]);
    setExpandedKey((cur) => (cur === key ? null : key));
  };

  return { expandedKey, toggle, page, setPage, orders, totalPages, loading, error };
}
